"use client";

import { useEffect, useMemo, useSyncExternalStore } from "react";
import LessonPlayer from "@/components/student/LessonPlayer";
import { useCourseNavigation, type CachedCourseData } from "@/lib/contexts/CourseNavigationContext";
import { hasOutlineModules, resolvePrefetchedFullCourse } from "@/lib/contexts/lesson-outline-resolver";
import { useQuery } from "@tanstack/react-query";
import { courseCoreQueryKey, fetchCourseCore } from "@/lib/student/course-core-query";
import type { CourseCard } from "@/lib/types/course";

type LessonPayload = {
    id: string;
    title: string;
    structuredContent?: unknown;
    moduleId: number;
    moduleTitle: string;
    canonicalLessonId: number;
    language: "English" | "Arabic";
    blockPersonalizationPending?: boolean;
};

function cachedToLessonPlayerCourse(c: CachedCourseData) {
    return {
        id: c.id,
        uuid: c.uuid,
        title: c.title,
        progress: c.progress,
        level: c.level,
        organizationName: c.organizationName,
        userDomain: c.userDomain,
        modules: c.modules.map((m) => ({
            id: m.id,
            title: m.title,
            isLocked: m.isLocked,
            earnedPoints: m.earnedPoints,
            hasModuleQuiz: m.hasModuleQuiz,
            moduleQuizPassed: m.moduleQuizPassed,
            moduleQuizAvailable: m.moduleQuizAvailable,
            level: m.level,
            lessons: m.lessons.map((l) => ({
                id: l.id,
                canonicalLessonId: l.canonicalLessonId,
                title: l.title,
                completed: l.completed,
                type: l.type ?? "lesson",
                hasContent: l.hasContent,
                isLocked: l.isLocked,
                duration: l.duration,
                earnedPoints: l.earnedPoints,
            })),
        })),
    };
}

/** Header + single-module outline so the shell paints before /core (or when list preview has no modules). */
function placeholderCourseForLesson(
    urlCourseId: string,
    lesson: LessonPayload,
    preview: CourseCard | null,
) {
    const courseId = preview?.id?.trim() ? preview.id : urlCourseId;
    return {
        id: courseId,
        title: preview?.title?.trim() ? preview.title : "Course",
        progress: typeof preview?.progress === "number" ? preview.progress : 0,
        level: preview?.level,
        organizationName: preview?.organizationName ?? null,
        userDomain: preview?.track?.trim() ? preview.track : null,
        modules: [
            {
                id: lesson.moduleId,
                title: lesson.moduleTitle,
                isLocked: false,
                level: null as { id: number; name: string; level_number: number } | null,
                lessons: [
                    {
                        id: lesson.id,
                        canonicalLessonId: lesson.canonicalLessonId,
                        title: lesson.title,
                        completed: false,
                        type: "lesson",
                        hasContent: true,
                        isLocked: false,
                        duration: "—",
                    },
                ],
            },
        ],
    };
}

export default function LessonPlayerWithCourseCache({
    urlCourseId,
    lesson,
    lessonIsLoading = false,
}: {
    urlCourseId: string;
    lesson: LessonPayload;
    lessonIsLoading?: boolean;
}) {
    const nav = useCourseNavigation();

    /** Match server HTML: CourseNav may hydrate list/core from sessionStorage on client before commit. */
    const isClientHydrated = useSyncExternalStore(
        () => () => {},
        () => true,
        () => false,
    );

    const outlineQuery = useQuery({
        queryKey: courseCoreQueryKey(urlCourseId),
        queryFn: () => fetchCourseCore(urlCourseId),
        staleTime: 0,
        refetchOnMount: "always",
    });

    const listPreview =
        isClientHydrated && urlCourseId && nav ? nav.getCourseListPreview(urlCourseId) ?? null : null;
    const prefetchedFull = useMemo(
        () =>
            isClientHydrated && urlCourseId && nav
                ? resolvePrefetchedFullCourse(nav, urlCourseId, lesson.id)
                : null,
        [isClientHydrated, nav, urlCourseId, lesson.id, nav?.prefetchedCourseData],
    );

    // Sync fresh query data into CourseNavigationContext
    useEffect(() => {
        if (!outlineQuery.data || !nav) return;
        const preview = nav.getCourseListPreview?.(urlCourseId) ?? null;
        const merged: CachedCourseData = {
            ...outlineQuery.data,
            certificates: outlineQuery.data.certificates ?? [],
            organizationName: outlineQuery.data.organizationName ?? preview?.organizationName ?? null,
            userDomain: outlineQuery.data.userDomain ?? (preview?.track?.trim() ? preview.track : null),
            coreFetchedAt: outlineQuery.dataUpdatedAt || Date.now(),
        };
        if ((!merged.track || merged.track === "General Track") && preview?.track?.trim()) {
            merged.track = preview.track;
        }
        nav.setPrefetchedCourseData(urlCourseId, merged);
        if (merged.id && merged.id !== urlCourseId) {
            nav.setPrefetchedCourseData(merged.id, merged);
        }
        if (merged.uuid && merged.uuid !== urlCourseId) {
            nav.setPrefetchedCourseData(merged.uuid, merged);
        }
        nav.setCourseListPreview?.(urlCourseId, null);
    }, [outlineQuery.data, outlineQuery.dataUpdatedAt, urlCourseId, nav]);

    const activeCourseData = useMemo<CachedCourseData | null>(() => {
        if (hasOutlineModules(outlineQuery.data)) {
            const preview = nav?.getCourseListPreview?.(urlCourseId) ?? null;
            const merged: CachedCourseData = {
                ...outlineQuery.data,
                certificates: outlineQuery.data.certificates ?? [],
                organizationName: outlineQuery.data.organizationName ?? preview?.organizationName ?? null,
                userDomain: outlineQuery.data.userDomain ?? (preview?.track?.trim() ? preview.track : null),
                coreFetchedAt: outlineQuery.dataUpdatedAt || Date.now(),
            };
            if ((!merged.track || merged.track === "General Track") && preview?.track?.trim()) {
                merged.track = preview.track;
            }
            return merged;
        }
        if (hasOutlineModules(prefetchedFull)) {
            return prefetchedFull;
        }
        return null;
    }, [outlineQuery.data, outlineQuery.dataUpdatedAt, prefetchedFull, urlCourseId, nav]);

    const outlineReady = activeCourseData !== null;

    const courseForPlayer = useMemo(() => {
        if (activeCourseData) {
            return cachedToLessonPlayerCourse(activeCourseData);
        }
        return placeholderCourseForLesson(urlCourseId, lesson, listPreview);
    }, [activeCourseData, urlCourseId, lesson, listPreview]);

    if (outlineQuery.error && !outlineReady) {
        return (
            <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4 text-center text-sm text-muted-foreground">
                <p>We couldn&apos;t load the course outline. Check your connection and try again.</p>
                <a
                    className="text-primary underline"
                    href={`/courses/${urlCourseId}`}
                >
                    Back to course
                </a>
            </div>
        );
    }

    return (
        <LessonPlayer
            key={lesson.id}
            lesson={lesson}
            course={courseForPlayer}
            outlineActionsDisabled={!outlineReady || lessonIsLoading}
            lessonLoading={lessonIsLoading}
        />
    );
}
